import mixpanel from "mixpanel-browser";
import { sanitizePath } from "../utils/sanitizePath";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const FIRST_DICTATION_KEY = "zt_mixpanel_first_dictation_completed_v1";
const DICTATION_COMPLETION_COUNT_KEY = "zt_mixpanel_dictation_completion_count_v1";
const SECOND_DICTATION_KEY = "zt_mixpanel_second_dictation_v1";
const MIXPANEL_API_HOST =
  process.env.REACT_APP_MIXPANEL_API_HOST || "https://api-eu.mixpanel.com";
const MIXPANEL_DEBUG = process.env.REACT_APP_MIXPANEL_DEBUG === "true";

let initialized = false;

const isBrowser = () => typeof window !== "undefined";

const normalizePathName = (pathName = "") => {
  const trimmed = String(pathName || "").trim();
  if (!trimmed || trimmed === "/") return "home";
  return trimmed.replace(/^\/+/, "").replace(/\//g, "_");
};

// mixpanel-browser stamps $current_url (window.location.href) and $referrer
// (document.referrer) onto every event. Checkout URLs carry a token, so any
// same-origin URL is reduced to origin + route template (query/hash dropped);
// external referrers are kept as they are.
const safeUrl = (href) => {
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return href;
    return `${url.origin}${sanitizePath(url.pathname)}`;
  } catch {
    return null;
  }
};

const safeUrlProps = () => {
  if (!isBrowser()) return {};
  const props = { $current_url: safeUrl(window.location.href) };
  if (document.referrer) props.$referrer = safeUrl(document.referrer);
  return props;
};

const resolveUserId = (user = {}) =>
  user?.user_id ||
  user?.UserId ||
  user?.id ||
  user?._id ||
  user?.EmailId ||
  user?.email ||
  null;

const resolvePlanType = (user = {}) =>
  user?.plan_type ||
  user?.planType ||
  user?.subscriptionPlan ||
  user?.plan ||
  user?.subscription?.plan ||
  "free";

const resolveSignupMethod = (user = {}) =>
  user?.signup_method ||
  user?.signupMethod ||
  user?.provider ||
  user?.authProvider ||
  "unknown";

export const initMixpanel = () => {
  if (initialized || !isBrowser()) return;
  if (!MIXPANEL_TOKEN) {
    if (MIXPANEL_DEBUG) {
      console.warn("[Mixpanel] Missing REACT_APP_MIXPANEL_TOKEN; init skipped.");
    }
    return;
  }

  const config = {
    track_pageview: false,
    persistence: "localStorage",
    debug: MIXPANEL_DEBUG,
  };
  if (MIXPANEL_API_HOST) {
    config.api_host = MIXPANEL_API_HOST;
  }

  mixpanel.init(MIXPANEL_TOKEN, config);

  mixpanel.register({
    device_type: "web",
    platform: "web",
  });

  if (MIXPANEL_DEBUG) {
    console.info("[Mixpanel] Initialized", {
      token_prefix: String(MIXPANEL_TOKEN).slice(0, 6),
      api_host: MIXPANEL_API_HOST || "default",
    });
  }

  initialized = true;
};

/**
 * @param {object} [options]
 * @param {boolean} [options.beforeNavigation] Set when the caller is about to
 *   leave the page synchronously (window.location.href = …). The SDK batches
 *   events by default, so the request is sent immediately via sendBeacon
 *   instead, which survives the unload.
 */
export const trackEvent = (eventName, props = {}, { beforeNavigation = false } = {}) => {
  if (!initialized) return;
  if (MIXPANEL_DEBUG) {
    console.info("[Mixpanel] track", eventName, props);
  }
  const properties = {
    device_type: "web",
    // Explicit props override the SDK's auto-collected raw URL/referrer.
    ...safeUrlProps(),
    ...props,
  };
  if (beforeNavigation) {
    mixpanel.track(eventName, properties, { transport: "sendBeacon", send_immediately: true });
    return;
  }
  mixpanel.track(eventName, properties);
};

export const trackPageViewed = (pathName, extraProps = {}) => {
  trackEvent("page_viewed", {
    page_name: normalizePathName(pathName),
    path_name: pathName,
    ...extraProps,
  });
};

/** The current page in `page_name` form ("login", "download_mac", "home"). */
export const currentPageName = () =>
  isBrowser() ? normalizePathName(sanitizePath(window.location.pathname)) : null;

export const identifyUser = (user = {}) => {
  if (!initialized || !isBrowser()) return;

  const userId = resolveUserId(user);
  if (!userId) return;
  const id = String(userId);

  const aliasKey = `zt_mixpanel_alias_done_${id}`;
  const aliasAlreadyDone = window.localStorage.getItem(aliasKey) === "1";

  if (!aliasAlreadyDone) {
    try {
      mixpanel.alias(id);
      window.localStorage.setItem(aliasKey, "1");
      if (MIXPANEL_DEBUG) {
        console.info("[Mixpanel] alias", id);
      }
    } catch {
      // ignore alias errors to avoid blocking identify
    }
  }

  if (MIXPANEL_DEBUG) {
    console.info("[Mixpanel] identify", id);
  }
  mixpanel.identify(id);
  mixpanel.people.set({
    plan_type: resolvePlanType(user),
    signup_method: resolveSignupMethod(user),
    device_first_used: "web",
    device_type: "web",
  });
};

export const trackFirstDictationCompletedOnce = (props = {}) => {
  if (!initialized || !isBrowser()) return;
  if (window.localStorage.getItem(FIRST_DICTATION_KEY) === "1") return;
  trackEvent("first_dictation_completed", props);
  window.localStorage.setItem(FIRST_DICTATION_KEY, "1");
};

// Call once per successful dictation, right after trackFirstDictationCompletedOnce.
// Fires `second_dictation_completed` (the apps' name) on the 2nd completion,
// once per browser.
export const trackSecondDictationOnce = (props = {}) => {
  if (!initialized || !isBrowser()) return;

  const currentCount = Number(
    window.localStorage.getItem(DICTATION_COMPLETION_COUNT_KEY) || "0"
  );
  const nextCount = currentCount + 1;
  window.localStorage.setItem(DICTATION_COMPLETION_COUNT_KEY, String(nextCount));

  if (window.localStorage.getItem(SECOND_DICTATION_KEY) === "1") return;
  if (nextCount !== 2) return;

  trackEvent("second_dictation_completed", props);
  window.localStorage.setItem(SECOND_DICTATION_KEY, "1");
};
